import { useMemo } from 'react';

import type { SecurityFocusFull } from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';

import { useGetOrganizationOptions } from '@apis/track-public/query-options';
import { Stack } from '@packages/fenrir/components/stack';
import { Typography } from '@packages/fenrir/components/typography';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import type { EmptyResultMessages } from '../../../../components/tables/_components/empty-result';
import {
  BlueprintsTable,
  BlueprintsTableColumn,
  parseBlueprintsForTable,
} from '../../../../components/tables/blueprints-table';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusUpcomingSpidrsTable
 * -----------------------------------------------------------------------------------------------*/
export type SecurityFocusBlueprintsTableProps = {
  focus: SecurityFocusFull;
};

export const SecurityFocusUpcomingSpidrsTable: React.FC<
  SecurityFocusBlueprintsTableProps
> = ({ focus }) => {
  const { t } = useSecurityJourneyTranslations();

  const { data: organization } = useQuery(useGetOrganizationOptions());

  const [isPrimaryTechnicalDomainVisible] = useFeatureFlag(
    'PRIMARY_TECHNICAL_DOMAIN',
  );

  const isOrganizationTierNonLegacy = useMemo(() => {
    return organization?.tierInfo && organization.tierInfo.legacy === false;
  }, [organization]);

  const tableColumns = useMemo(() => {
    let tempTableColumns = [
      BlueprintsTableColumn.Name,
      BlueprintsTableColumn.Tier,
      BlueprintsTableColumn.TechnicalDomains,
      BlueprintsTableColumn.Technologies,
      BlueprintsTableColumn.Nist,
      BlueprintsTableColumn.Nist2,
      BlueprintsTableColumn.Cis,
      BlueprintsTableColumn.Subscriptions,
    ];
    if (isOrganizationTierNonLegacy) {
      return tempTableColumns;
    }
    tempTableColumns = tempTableColumns.filter((c) => c !== 'tier');
    return tempTableColumns;
  }, [isOrganizationTierNonLegacy]);

  const blueprints = parseBlueprintsForTable({
    blueprints: focus.blueprints,
    isPrimaryTechnicalDomainVisible,
  });

  const emptyResultMessages: EmptyResultMessages = {
    title: t('focusID.securityFocusUpcomingSpidrsTable.noResultMessage.title'),
    description: t(
      'focusID.securityFocusUpcomingSpidrsTable.noResultMessage.description',
    ),
  };

  return (
    <Stack
      spacing={3}
      width="100%"
      data-testid="security-focus-upcoming-spidrs-table"
    >
      <Typography variant="h3">
        {t('focusID.securityFocusUpcomingSpidrsTable.title')}
      </Typography>
      <BlueprintsTable
        blueprints={blueprints}
        tableColumns={tableColumns}
        emptyResultMessages={emptyResultMessages}
        isPrimaryTechnicalDomainVisible={isPrimaryTechnicalDomainVisible}
      />
    </Stack>
  );
};
